/**
 * Email templates + signature endpoints (June 2026) — outbound email library.
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/email_templates.py``.
 * Powers the Settings → "Modelli email" manager (org-shared + personal
 * templates) and the per-user signature override (effective = user ?? org),
 * plus the template picker fused into the cold-email / email HITL composer
 * and the prospecting bulk-send.
 *
 * Ando is a system of action: these templates are reusable bodies the agent
 * (cold bulk) and the user (single composer) drop into a real send — never a
 * marketing campaign builder.
 */

import { apiClient } from "../ApiClient.js";

// ─── Types ───────────────────────────────────────────────────────────────

/**
 * Which composer surfaces a template targets:
 *   - ``cold``     — outbound prospecting / cold email only.
 *   - ``standard`` — replies / standard sends only.
 *   - ``both``     — surfaces everywhere.
 */
export type EmailTemplateKind = "cold" | "standard" | "both";

export interface EmailTemplate {
  id: string;
  /** ``null`` = org library (shared); set = personal template. */
  owner_user_id: string | null;
  name: string;
  subject: string;
  body: string;
  kind: EmailTemplateKind;
  /** Append the effective signature when this template is used. */
  append_signature: boolean;
  /** Detected ``{{merge_field}}`` placeholders the BE re-renders on send. */
  merge_fields: string[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CreateEmailTemplateInput {
  name: string;
  subject: string;
  body: string;
  kind: EmailTemplateKind;
  /** Save into the org library (shared) instead of the personal library. */
  is_org: boolean;
  append_signature: boolean;
}

/** Partial update — any subset of the editable fields. */
export interface UpdateEmailTemplateInput {
  name?: string;
  subject?: string;
  body?: string;
  kind?: EmailTemplateKind;
  append_signature?: boolean;
}

export interface EmailSignature {
  signature_html: string;
}

export interface EffectiveSignature {
  effective_html: string;
  user_html: string;
  org_html: string;
  /** ``account`` wins when the FROM mailbox has a per-account default (pool
   *  model); otherwise the BE resolves ``user`` → ``org`` → ``none``. */
  source: "account" | "user" | "org" | "none";
  /**
   * Id of the pool signature that resolved (multi-signature "pool" model,
   * Jul 2026). ``null``/absent when the effective HTML comes from the legacy
   * per-user override or the org Brand Kit rather than a named pool row.
   */
  signature_id?: string | null;
}

// NOTE: the inline AI text-edit client (``aiEditEmailText`` for
// ``POST /email/ai-edit``) lives in its dedicated module ``./emailEdit.ts`` —
// do NOT re-declare it here (DRY).

// ─── Templates ─────────────────────────────────────────────────────────────

/** GET ``/email/templates`` — org library + my personal templates. */
export async function listEmailTemplates(
  kind: EmailTemplateKind = "both",
): Promise<EmailTemplate[]> {
  return await apiClient.get<EmailTemplate[]>(
    `/email/templates?kind=${encodeURIComponent(kind)}`,
  );
}

/** POST ``/email/templates`` — create an org or personal template. */
export async function createEmailTemplate(
  input: CreateEmailTemplateInput,
): Promise<EmailTemplate> {
  return await apiClient.post<EmailTemplate>("/email/templates", input);
}

/** PUT ``/email/templates/{id}`` — partial update. */
export async function updateEmailTemplate(
  id: string,
  input: UpdateEmailTemplateInput,
): Promise<{ status: string }> {
  return await apiClient.put<{ status: string }>(
    `/email/templates/${encodeURIComponent(id)}`,
    input,
  );
}

/** DELETE ``/email/templates/{id}``. */
export async function deleteEmailTemplate(
  id: string,
): Promise<{ status: string }> {
  return await apiClient.delete<{ status: string }>(
    `/email/templates/${encodeURIComponent(id)}`,
  );
}

// ─── Signature ─────────────────────────────────────────────────────────────

/** GET ``/email/signature`` — my per-user override (may be empty). */
export async function getEmailSignature(): Promise<EmailSignature> {
  return await apiClient.get<EmailSignature>("/email/signature");
}

/** PUT ``/email/signature`` — set my per-user override. */
export async function setEmailSignature(
  signatureHtml: string,
): Promise<{ status: string }> {
  return await apiClient.put<{ status: string }>("/email/signature", {
    signature_html: signatureHtml,
  });
}

/**
 * GET ``/email/signature/effective`` — the resolved signature (account ?? user
 * ?? org) plus both raw layers + which one wins (``source``). Drives the
 * read-only "firma effettiva" preview in Settings and the composer toggle.
 *
 * Pass ``account`` (the FROM mailbox, e.g. ``andrea@acme.com``) to prefer that
 * mailbox's signature when one exists; omit it for the account-agnostic
 * resolution (backward-compatible).
 */
export async function getEffectiveSignature(
  account?: string,
): Promise<EffectiveSignature> {
  const qs = account
    ? `?account=${encodeURIComponent(account)}`
    : "";
  return await apiClient.get<EffectiveSignature>(
    `/email/signature/effective${qs}`,
  );
}

/**
 * Upload an image for use INSIDE the email signature (PNG/JPEG/WebP/GIF,
 * max 2MB). Returns the RELATIVE api url — embed as
 * ``<img src="${getEnv().apiUrl}${url}">``; at send time the BE swaps it for a
 * CID inline attachment so it renders for every recipient.
 */
export async function uploadSignatureImage(
  file: File,
): Promise<{ url: string; token: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  return apiClient.postMultipart<{ url: string; token: string }>(
    "/email/signature/image",
    form,
  );
}
